import { useToast } from '@retail/hooks';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppTFunction } from '@retail/app/i18n';
import { useMutatePostCustomerMarkupConditions } from '@retail/calculus/data-access';
import { CustomerMarkupConditionsPost } from '@retail/customer-markup/types';

interface Props {
  assortmentIds: number[];
}

export const useCustomerMarkupCreate = ({ assortmentIds }: Props) => {
  const t = useAppTFunction();

  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: postCustomerMarkupConditions, isPending: isLoadingPost } =
    useMutatePostCustomerMarkupConditions({ assortmentIds: assortmentIds });

  const handleInvalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['draft-conditions'] });
    queryClient.invalidateQueries({ queryKey: ['product-groups-hierarchy'] });
  }, [queryClient]);

  const createCustomerMarkupCondition = (customerMarkup: CustomerMarkupConditionsPost) => {
    postCustomerMarkupConditions({
      body: [customerMarkup],
    })
      .then(() => {
        toast.success(t('calculus.actions.conditionCreateInDrawer'));
        handleInvalidateQueries();
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('common.genericError'));
      });
  };

  const createCustomerMarkupConditions = (supplierConditions: CustomerMarkupConditionsPost[]) => {
    postCustomerMarkupConditions({
      body: supplierConditions,
    })
      .then(() => {
        toast.success(t('calculus.actions.conditionCreateInDrawer'));
        handleInvalidateQueries();
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('common.genericError'));
      });
  };

  return {
    createCustomerMarkupCondition,
    createCustomerMarkupConditions,
    isLoadingPost,
  };
};
